import type { RouteMonitoringVisit } from '@/pages/Business/pages/Monitoring/models';
import { VisitStatus } from '@/pages/Business/pages/Monitoring/enums';
import { normalizeCommerceBlock } from './normalize-commerce-block';
import { asRecord, pickNum, pickStr } from './object-pickers';
import { normalizeAddress } from './normalize-visit-address';
import {
  coalesceShippingsRaw,
  normalizeVisitShipping,
} from './normalize-visit-shipping';

export function normalizeRouteMonitoringVisit(
  raw: unknown
): RouteMonitoringVisit {
  const o = asRecord(raw) ?? {};
  const shippingsRaw = coalesceShippingsRaw(o);
  const shippings =
    shippingsRaw.length > 0
      ? shippingsRaw.map((item) => normalizeVisitShipping(item))
      : undefined;

  let visitCommerce = normalizeCommerceBlock(o.commerce);
  if (!visitCommerce?.name) {
    const flatCommerceName = pickStr(o, 'commerceName', 'commerce_name');
    if (flatCommerceName) {
      visitCommerce = { id: undefined, name: flatCommerceName };
    }
  }

  const order =
    pickNum(o, 'order', 'visit_order') ??
    pickNum(o, 'visitOrder', 'visit_order') ??
    0;

  const shippingNumber = pickNum(o, 'shippingNumber', 'shipping_number') ?? 0;

  const packagesCount = pickNum(o, 'packagesCount', 'packages_count') ?? 0;

  const packagesPickedRaw = asRecord(o.packagesPicked ?? o.packages_picked);
  const packagesPicked = packagesPickedRaw
    ? {
        departure: pickNum(packagesPickedRaw, 'departure', 'departure') ?? 0,
        arrival: pickNum(packagesPickedRaw, 'arrival', 'arrival') ?? 0,
      }
    : undefined;

  const distanceMeters =
    pickNum(o, 'distanceMeters', 'distance_meters') ?? undefined;
  const travelTime = pickNum(o, 'travelTime', 'travel_time') ?? undefined;
  const distanceFromPrevious =
    pickNum(o, 'distanceFromPrevious', 'distance_from_previous') ?? undefined;
  const durationFromPrevious =
    pickNum(o, 'durationFromPrevious', 'duration_from_previous') ?? undefined;

  const visitLat = pickNum(o, 'lat', 'latitude');
  const visitLong = pickNum(o, 'long', 'longitude');
  const originalLat = pickNum(o, 'originalLat', 'original_lat');
  const originalLong = pickNum(o, 'originalLong', 'original_long');
  const coordinatesChangedRaw =
    o.coordinatesChanged ?? o.coordinates_changed ?? null;
  const coordinatesChanged =
    coordinatesChangedRaw === true ||
    coordinatesChangedRaw === 'true' ||
    coordinatesChangedRaw === 1
      ? true
      : coordinatesChangedRaw === false ||
          coordinatesChangedRaw === 'false' ||
          coordinatesChangedRaw === 0
        ? false
        : null;

  return {
    id: String(o.id ?? ''),
    order,
    scheduledFrom: pickStr(o, 'scheduledFrom', 'scheduled_from') ?? '',
    scheduledTo: pickStr(o, 'scheduledTo', 'scheduled_to') ?? '',
    visitedAt: pickStr(o, 'visitedAt', 'visited_at'),
    status:
      o.status !== undefined && o.status !== null
        ? (String(o.status) as VisitStatus)
        : VisitStatus.PENDING,
    shippingNumber,
    ...(visitLat !== undefined ? { lat: visitLat } : {}),
    ...(visitLong !== undefined ? { long: visitLong } : {}),
    ...(originalLat !== undefined ? { originalLat } : {}),
    ...(originalLong !== undefined ? { originalLong } : {}),
    ...(coordinatesChanged !== null ? { coordinatesChanged } : {}),
    address: normalizeAddress(o.address),
    packagesCount,
    ...(packagesPicked ? { packagesPicked } : {}),
    externalId: pickStr(o, 'externalId', 'external_id'),
    recipient: pickStr(o, 'recipient', 'recipient'),
    recipientName: pickStr(o, 'recipientName', 'recipient_name'),
    shippings,
    commerce: visitCommerce,
    distanceMeters: distanceMeters ?? null,
    travelTime: travelTime ?? null,
    distanceFromPrevious: distanceFromPrevious ?? null,
    durationFromPrevious: durationFromPrevious ?? null,
  };
}

export function normalizeRouteMonitoringVisitsFromApi(
  rawVisits: unknown[]
): RouteMonitoringVisit[] {
  return rawVisits.map((v) => normalizeRouteMonitoringVisit(v));
}
